import { apiFetch } from "./http";
import { apiUrl } from "./config";
import type { ParsedInvoiceDraft, ParsedInvoiceLine } from "../features/invoice-import/types";
import {
  draftToCreatePayload,
  draftToPatchPayload,
  mapImportBundleToCandidate,
  mapListItemToCandidate,
} from "./mappers/invoiceImportMapper";
import type { ApiCreateEventResult, ApiImportBundle, ApiImportListItem } from "./invoiceImportTypes";
import type { InvoiceImportCandidate } from "../features/invoice-import/types";

export async function listInvoiceImports(filter: "active" | "imported"): Promise<InvoiceImportCandidate[]> {
  const response = await apiFetch<{ items: ApiImportListItem[] }>(
    `/invoice-imports?filter=${filter}`,
  );
  return response.items.map(mapListItemToCandidate);
}

export async function getInvoiceImport(importId: string): Promise<InvoiceImportCandidate> {
  const bundle = await apiFetch<ApiImportBundle>(`/invoice-imports/${importId}`);
  return mapImportBundleToCandidate(bundle);
}

export async function createInvoiceImportFromDraft(params: {
  source: InvoiceImportCandidate["source"];
  draft: ParsedInvoiceDraft;
}): Promise<InvoiceImportCandidate> {
  const payload = draftToCreatePayload(params);
  const bundle = await apiFetch<ApiImportBundle>("/invoice-imports", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return mapImportBundleToCandidate(bundle);
}

export async function uploadInvoiceSourceDocument(importId: string, file: Blob, filename: string): Promise<void> {
  const form = new FormData();
  form.append("file", file, filename);
  await apiFetch(`/invoice-imports/${importId}/source-document`, {
    method: "POST",
    body: form,
  });
}

export async function patchInvoiceDraft(importId: string, draft: ParsedInvoiceDraft): Promise<InvoiceImportCandidate> {
  const bundle = await apiFetch<ApiImportBundle>(`/invoice-imports/${importId}/draft`, {
    method: "PATCH",
    body: JSON.stringify(draftToPatchPayload(draft)),
  });
  return mapImportBundleToCandidate(bundle);
}

export async function patchInvoiceLine(
  importId: string,
  lineId: string,
  patch: Partial<ParsedInvoiceLine>,
): Promise<InvoiceImportCandidate> {
  const bundle = await apiFetch<ApiImportBundle>(`/invoice-imports/${importId}/lines/${lineId}`, {
    method: "PATCH",
    body: JSON.stringify({
      name: patch.name,
      description: patch.description,
      interpretedUnit: patch.interpretedUnit,
      interpretedQuantity: patch.interpretedQuantity,
      classification: patch.classification,
      needsReview: patch.needsReview,
    }),
  });
  return mapImportBundleToCandidate(bundle);
}

export async function markInvoiceImportReady(importId: string): Promise<InvoiceImportCandidate> {
  const bundle = await apiFetch<ApiImportBundle>(`/invoice-imports/${importId}/mark-ready`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  return mapImportBundleToCandidate(bundle);
}

export async function createEventFromImport(importId: string): Promise<ApiCreateEventResult> {
  return apiFetch<ApiCreateEventResult>(`/invoice-imports/${importId}/create-event`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function fetchInvoiceSourceDocument(importId: string): Promise<Response> {
  return fetch(apiUrl(`/invoice-imports/${importId}/source-document`), { credentials: "include" });
}

export async function loadAllInvoiceImportQueueItems(): Promise<InvoiceImportCandidate[]> {
  const [active, imported] = await Promise.all([
    listInvoiceImports("active"),
    listInvoiceImports("imported"),
  ]);
  const merged = new Map<string, InvoiceImportCandidate>();
  for (const item of [...active, ...imported]) {
    merged.set(item.id, item);
  }
  return [...merged.values()];
}
